import { EditGridView } from '../../../types/edit-grid';
import { EditGridProps } from '../../components/EditGrid';
import { formatPrice } from '../../number-utils';
import { Api } from '../api';
import { getEditGridModalProps } from './EditGridModal';
import { openProductModal, updateGridSize } from './product-grid-utils';

const save = async (view: EditGridView) => {
    const { state } = view;

    const reqBody = {
        products: state.grid.products,
    };

    const response = await Api.postJson(state.urls.saveGrid, state.csrfToken, reqBody);

    if (response.ok) {
        window.toastr.success(state.lang.notifications.savingGridSucceeded);
        state.anyChanges = false;
    } else {
        window.toastr.error(state.lang.notifications.savingGridFailed);
    }
};

export const getEditGridProps = (view: EditGridView): EditGridProps => {
    const { state, update } = view;
    const { grid: stateGrid, products, currencySymbol, lang } = state;

    const gridProducts = stateGrid.products.map((productRow) =>
        productRow.map((product) => {
            if (product == null) {
                return null;
            }

            // Handle product variants
            if (product.variantId) {
                const foundProduct = products.products.find((p) => p.variants.find((v) => v.id == product.variantId));
                if (!foundProduct) {
                    return null;
                }

                const productVariant = foundProduct.variants.find((v) => v.id == product.variantId);
                if (!productVariant) {
                    return null;
                }

                return {
                    title: product.title,
                    color: product.color,
                    price: formatPrice(productVariant.unitPrice, currencySymbol),
                    active: product.active && !productVariant.deleted,
                };
            }

            // Handle serial tickets
            if (product.serialTicketTypeId) {
                const serialTicket = products.serialTickets.find((t) => t.id == product.serialTicketTypeId);
                if (!serialTicket) {
                    return null;
                }

                return {
                    title: product.title,
                    color: product.color,
                    price: formatPrice(serialTicket.price, currencySymbol),
                    active: product.active && !serialTicket.deleted,
                };
            }

            // Handle gift cards
            if (product.giftCardTypeId) {
                const giftCard = products.giftCards.find((g) => g.id == product.giftCardTypeId);
                if (!giftCard) {
                    return null;
                }

                return {
                    title: product.title,
                    color: product.color,
                    price: giftCard.price === null ? null : formatPrice(giftCard.price, currencySymbol),
                    active: product.active && !giftCard.deleted,
                };
            }

            // Handle membership cards
            if (product.cardTypeId) {
                const membershipCard = products.membershipCards.find(
                    (m) => m.id == product.cardTypeId && m.isRenewal == product.isRenewal
                );
                if (!membershipCard) {
                    return null;
                }

                return {
                    title: product.title,
                    color: product.color,
                    price: formatPrice(membershipCard.price, currencySymbol),
                    active: product.active && !membershipCard.deleted,
                };
            }

            return null;
        })
    );

    return {
        editModal: getEditGridModalProps(view),
        navigation: state.pages,
        onSave: () => {
            save(view);
        },
        saveButtonLabel: lang.common.save,
        grid: {
            title: stateGrid.title,
            name: stateGrid.title.toLowerCase(),
            products: gridProducts,
            renderEmptyCards: true,
            dragCurrentRow: state.currentDragRow,
            dragTargetCol: state.currentDragCol,
            onDrag: (row1, col1, row2, col2) => {
                state.anyChanges = true;
                const firstProduct = state.grid.products[row1][col1];
                const secondProduct = state.grid.products[row2][col2];
                const shouldUpdateGrid = (firstProduct && !secondProduct) || (!firstProduct && secondProduct);
                state.grid.products[row1][col1] = secondProduct;
                state.grid.products[row2][col2] = firstProduct;
                if (shouldUpdateGrid && row1 !== row2 && Math.max(row1, row2) - state.grid.products.length < 2) {
                    updateGridSize(view);
                }
                update();
            },
            onClick: (row, col) => openProductModal(view, row, col),
            onDragStart: (row, col) => {
                state.currentDragRow = row;
                state.currentDragCol = col;
                update();
            },
        },
    };
};
